import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import clsx from "clsx";
import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { Spinner } from "@/components/ui/Spinner";
import { auftragMiniFromProject } from "@/lib/orderDraftUtils";
import { listOrdersWithFallback } from "@/lib/orders";
import type { OrderHistoryLine, OrderHistoryRow } from "@/lib/orders";
import { WerkszeugnisBadge } from "@/pages/bestellungen/OrdersHistorySection";
import type { OrderedMaterialLine } from "@/lib/projectOverview";
import type { Order } from "@/lib/projects";
import { useOrderWizard } from "@/pages/bestellungen/useOrderWizard";
import { fmtOrderDate } from "@/pages/bestellungen/orderHistoryDisplay";
import { orderLineDisplay } from "@/projectOverview/orderLineDisplay";
import { useRealtime } from "@/lib/useRealtime";

function fmtDateTime(value: string | undefined | null): string {
  if (!value) return "—";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString("de-DE");
}

function fmtMoney(value: number | null | undefined, currency?: string | null): string | null {
  if (value == null || !Number.isFinite(value)) return null;
  try {
    return new Intl.NumberFormat("de-DE", {
      style: "currency",
      currency: currency || "EUR",
    }).format(value);
  } catch {
    return `${value} ${currency || "EUR"}`;
  }
}

function splitBestellnummern(raw: string | null | undefined): string[] {
  return String(raw || "")
    .split(/[,;]+/)
    .map((s) => s.trim())
    .filter(Boolean);
}

function submissionChannelLabel(channel: string | null | undefined): string | null {
  switch (channel) {
    case "graph":
      return "Direkt (Microsoft)";
    case "mailto":
      return "Mail-Programm";
    case "copy":
      return "Nummer kopiert";
    default:
      return null;
  }
}

function deliveryLabel(order: OrderHistoryRow): string | null {
  if (order.lieferart === "abholung") return "Abholung";
  const abk = (order.lieferadresse_abkuerzung || "").trim();
  return abk ? `Lieferung · ${abk}` : "Lieferung";
}

function filterOrdersForProject(orders: OrderHistoryRow[], auftragId: number): OrderHistoryRow[] {
  return orders
    .filter((order) => {
      const inProjects = (order.projects || []).some((p) => Number(p.auftrag_id) === auftragId);
      const inLines = (order.positionen || []).some((p) => Number(p.auftrag_id) === auftragId);
      return inProjects || inLines;
    })
    .map((order) => ({
      ...order,
      positionen: (order.positionen || []).filter((p) => Number(p.auftrag_id) === auftragId),
    }));
}

function countDistinctOrders(lines: OrderedMaterialLine[]): number {
  const ids = new Set<number>();
  for (const line of lines) {
    if (line.order_id != null) ids.add(Number(line.order_id));
  }
  return ids.size;
}

function PositionRow({
  line,
  usedTimesheetId,
}: {
  line: OrderHistoryLine;
  usedTimesheetId?: number | null;
}) {
  const qty =
    line.menge != null
      ? new Intl.NumberFormat("de-DE", { maximumFractionDigits: 4 }).format(Number(line.menge))
      : null;
  const price = fmtMoney(line.angebot_preis ?? null, line.angebot_waehrung);

  return (
    <tr className="eh-project-orders-table__row">
      <td className="eh-project-orders-table__qty tabular-nums">{qty || "—"}</td>
      <td className="eh-project-orders-table__unit">{line.einheit || "—"}</td>
      <td className="eh-project-orders-table__text">{line.text || "—"}</td>
      <td className="eh-project-orders-table__hero font-mono text-xs">{line.hero_nr || "—"}</td>
      <td className="font-mono text-xs">{line.lot_nummer || "—"}</td>
      <td className="eh-project-orders-table__price tabular-nums">{price || "—"}</td>
      <td className="eh-project-orders-table__meta text-xs text-label-secondary">
        {usedTimesheetId ? (
          <Link to={`/stundennachweise?open=${usedTimesheetId}`} className="text-accent hover:underline">
            SN #{usedTimesheetId}
          </Link>
        ) : (
          "—"
        )}
      </td>
    </tr>
  );
}

function usedTimesheetForLine(
  line: OrderHistoryLine,
  materialLines: OrderedMaterialLine[],
): number | null {
  const match = materialLines.find((mat) => {
    const lineText = String(line.text || "").trim();
    const matText = String(mat.text || "").trim();
    const sameText = lineText === matText;
    const sameHero = String(mat.hero_nr || "").trim() === String(line.hero_nr || "").trim();
    const sameBn = String(mat.bestellnummer || "").trim() === String(line.bestellnummer || "").trim();
    return sameText && sameHero && sameBn;
  });
  const used = match?.used_timesheet_id;
  return used != null && Number.isFinite(Number(used)) ? Number(used) : null;
}

function ProjectOrderCard({
  order,
  materialLines,
}: {
  order: OrderHistoryRow;
  materialLines: OrderedMaterialLine[];
}) {
  const cancelled = Boolean(order.cancelled_at);
  const tkList = splitBestellnummern(order.bestellnummern);
  const contact = (order.supplier_contact_name || "").trim();
  const channelLabel = submissionChannelLabel(order.submission_channel);
  const items = Array.isArray(order.positionen) ? order.positionen : [];

  const taNums: string[] = [];
  for (const l of order.anfrage_links || []) {
    for (const n of l.ta_nummern || []) if (n) taNums.push(n);
  }
  const uniqTa = [...new Set(taNums)].join(", ");

  return (
    <article
      className={clsx(
        "eh-project-order-card rounded-ios-lg border border-separator bg-card",
        cancelled && "eh-project-order-card--cancelled",
      )}
    >
      <header className="eh-project-order-card__header border-b border-separator px-4 py-3 sm:px-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0 space-y-1">
            <div className="flex flex-wrap items-center gap-2">
              {tkList.length ? (
                tkList.map((tk) => (
                  <span
                    key={tk}
                    className="rounded-md bg-fill-tertiary px-2 py-0.5 font-mono text-xs font-semibold text-label"
                  >
                    {tk}
                  </span>
                ))
              ) : (
                <span className="text-sm font-semibold text-label">Ohne Bestellnummer</span>
              )}
              {cancelled ? (
                <span className="rounded-full border border-status-red/30 bg-status-red/15 px-2 py-0.5 text-xs font-semibold text-status-red">
                  Storniert
                </span>
              ) : order.geliefert_at ? (
                <span className="rounded-full border border-status-green/40 bg-status-green/15 px-2 py-0.5 text-xs font-semibold text-status-green">
                  Geliefert · {fmtOrderDate(order.geliefert_at)}
                </span>
              ) : (
                <span className="rounded-full border border-status-orange/40 bg-status-orange/15 px-2 py-0.5 text-xs font-semibold text-status-orange">
                  Offen
                </span>
              )}
              {Number(order.werkszeugnis_count || 0) > 0 ? <WerkszeugnisBadge /> : null}
            </div>
            <p className="text-sm font-medium text-label">{order.supplier_name || "—"}</p>
            <p className="text-xs text-label-secondary">
              {[contact, order.supplier_email].filter(Boolean).join(" · ") || "Kein Ansprechpartner"}
            </p>
          </div>
          <div className="text-right text-xs text-label-secondary">
            <p>{fmtDateTime(order.created_at)}</p>
            <p>{items.length} Position{items.length === 1 ? "" : "en"}</p>
          </div>
        </div>

        <dl className="mt-3 grid gap-2 text-xs sm:grid-cols-2 lg:grid-cols-4">
          <div>
            <dt className="text-label-tertiary">Betreff</dt>
            <dd className="text-label">{order.subject || "—"}</dd>
          </div>
          <div>
            <dt className="text-label-tertiary">Lieferung</dt>
            <dd className="text-label">{deliveryLabel(order) || "—"}</dd>
          </div>
          <div>
            <dt className="text-label-tertiary">Versand</dt>
            <dd className="text-label">{channelLabel || "—"}</dd>
          </div>
          <div>
            <dt className="text-label-tertiary">Kürzel</dt>
            <dd className="text-label">{order.kuerzel || "—"}</dd>
          </div>
          {order.source_ma?.id ? (
            <div>
              <dt className="text-label-tertiary">Materialanforderung</dt>
              <dd>
                <Link
                  to={`/materialanforderungen?id=${order.source_ma.id}`}
                  className="text-accent hover:underline"
                >
                  MA {order.source_ma.ma_nummer || `#${order.source_ma.id}`}
                </Link>
              </dd>
            </div>
          ) : null}
          {uniqTa ? (
            <div>
              <dt className="text-label-tertiary">Technische Anfrage</dt>
              <dd>
                <a href={`/anfragen?q=${encodeURIComponent(uniqTa)}`} className="text-accent hover:underline">
                  TA {uniqTa}
                </a>
              </dd>
            </div>
          ) : null}
          {Number(order.change_log_count || 0) > 0 ? (
            <div>
              <dt className="text-label-tertiary">Änderungen</dt>
              <dd className="text-label">{order.change_log_count} protokolliert</dd>
            </div>
          ) : null}
        </dl>
      </header>

      <div className="overflow-x-auto px-4 py-3 sm:px-5">
        <table className="eh-project-orders-table w-full min-w-[640px] text-sm">
          <thead>
            <tr className="text-left text-xs text-label-tertiary">
              <th className="pb-2 pr-3 font-medium">Menge</th>
              <th className="pb-2 pr-3 font-medium">Einheit</th>
              <th className="pb-2 pr-3 font-medium">Bezeichnung</th>
              <th className="pb-2 pr-3 font-medium">Hero-Nr.</th>
              <th className="pb-2 pr-3 font-medium">LOT-Nr.</th>
              <th className="pb-2 pr-3 font-medium">Angebot</th>
              <th className="pb-2 font-medium">Stundennachweis</th>
            </tr>
          </thead>
          <tbody>
            {items.length ? (
              items.map((line, idx) => (
                <PositionRow
                  key={`${order.id}-${idx}`}
                  line={line}
                  usedTimesheetId={usedTimesheetForLine(line, materialLines)}
                />
              ))
            ) : (
              <tr>
                <td colSpan={7} className="py-4 text-label-secondary">
                  Keine Positionen für dieses Projekt.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <footer className="flex flex-wrap items-center justify-between gap-2 border-t border-separator px-4 py-2.5 text-xs sm:px-5">
        <span className="text-label-tertiary">Bestell-ID {order.id}</span>
        <Link
          to={`/bestellungen?q=${encodeURIComponent(tkList[0] || order.subject || "")}`}
          className="font-medium text-accent hover:underline"
        >
          In Bestellungen öffnen
        </Link>
      </footer>
    </article>
  );
}

export function ProjectOrdersTab({
  auftragId,
  auftragsnummer,
  materialLines,
  project,
}: {
  auftragId: number;
  auftragsnummer?: string | null;
  materialLines: OrderedMaterialLine[];
  project: Order;
}) {
  const { openNew, subscribeOrdersChanged } = useOrderWizard();
  const [orders, setOrders] = useState<OrderHistoryRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (options?: { silent?: boolean }) => {
    if (!auftragsnummer?.trim()) {
      setOrders([]);
      setLoading(false);
      return;
    }
    if (!options?.silent) setLoading(true);
    setError(null);
    try {
      const { rows } = await listOrdersWithFallback({ q: auftragsnummer.trim() });
      setOrders(filterOrdersForProject(rows, auftragId));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Bestellungen konnten nicht geladen werden.");
      if (!options?.silent) setOrders([]);
    } finally {
      setLoading(false);
    }
  }, [auftragId, auftragsnummer]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    return subscribeOrdersChanged(() => {
      void load({ silent: true });
    });
  }, [subscribeOrdersChanged, load]);

  // Realtime-Änderungen still einpflegen – der Tab bleibt dabei sichtbar.
  useRealtime({
    scopes: ["orders:list"],
    domains: ["orders"],
    events: ["orders_changed"],
    onChange: () => {
      void load({ silent: true });
    },
  });

  const stats = useMemo(() => {
    const positions = orders.reduce((sum, o) => sum + (o.positionen?.length || 0), 0);
    const cancelled = orders.filter((o) => o.cancelled_at).length;
    const materialCount = materialLines.length;
    return { orders: orders.length, positions, cancelled, materialCount };
  }, [orders, materialLines]);

  const linesByOrderId = useMemo(() => {
    const map = new Map<number, OrderedMaterialLine[]>();
    for (const line of materialLines) {
      const oid = Number(line.order_id);
      if (!Number.isFinite(oid)) continue;
      if (!map.has(oid)) map.set(oid, []);
      map.get(oid)!.push(line);
    }
    return map;
  }, [materialLines]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Spinner />
        <span className="ml-2 text-sm text-label-secondary">Lade Bestellungen …</span>
      </div>
    );
  }

  if (error) {
    return <Alert variant="error">{error}</Alert>;
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-end gap-2">
        <Button
          type="button"
          size="sm"
          onClick={() => openNew({ auftrag: auftragMiniFromProject(project) })}
        >
          Neue Bestellung
        </Button>
      </div>
      <div className="eh-project-orders-stats grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
        <div className="rounded-ios border border-separator bg-card px-3 py-2.5">
          <p className="text-xs text-label-tertiary">Bestellungen</p>
          <p className="text-lg font-semibold tabular-nums text-label">{stats.orders}</p>
        </div>
        <div className="rounded-ios border border-separator bg-card px-3 py-2.5">
          <p className="text-xs text-label-tertiary">Positionen (Projekt)</p>
          <p className="text-lg font-semibold tabular-nums text-label">{stats.positions}</p>
        </div>
        <div className="rounded-ios border border-separator bg-card px-3 py-2.5">
          <p className="text-xs text-label-tertiary">Materialzeilen</p>
          <p className="text-lg font-semibold tabular-nums text-label">{stats.materialCount}</p>
        </div>
        <div className="rounded-ios border border-separator bg-card px-3 py-2.5">
          <p className="text-xs text-label-tertiary">Storniert</p>
          <p className="text-lg font-semibold tabular-nums text-label">{stats.cancelled}</p>
        </div>
      </div>

      {!orders.length ? (
        <div className="rounded-ios-lg border border-separator bg-card px-4 py-10 text-center">
          <p className="text-sm text-label-secondary">Keine Bestellungen für dieses Projekt gefunden.</p>
          {materialLines.length ? (
            <div className="mx-auto mt-6 max-w-2xl text-left">
              <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-label-tertiary">
                Materialzeilen aus der Projektübersicht
              </p>
              <ul className="space-y-2 text-sm">
                {materialLines.map((line, idx) => {
                  const { qty, unit, title } = orderLineDisplay(line);
                  return (
                    <li
                      key={`${line.order_line_id ?? line.id ?? idx}`}
                      className="rounded-ios border border-separator bg-fill-tertiary/40 px-3 py-2"
                    >
                      <span className="font-mono text-xs text-label-secondary">
                        {line.bestellnummer || "—"}
                      </span>
                      <span className="mx-2 text-label-tertiary">·</span>
                      {qty ? <span className="tabular-nums">{qty} </span> : null}
                      {unit ? <span>{unit} </span> : null}
                      <span>{title}</span>
                    </li>
                  );
                })}
              </ul>
            </div>
          ) : null}
        </div>
      ) : (
        <div className="space-y-4">
          {orders.map((order) => (
            <ProjectOrderCard
              key={order.id}
              order={order}
              materialLines={linesByOrderId.get(order.id) || materialLines.filter((l) => Number(l.order_id) === order.id)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export function countProjectOrders(materialLines: OrderedMaterialLine[]): number {
  return countDistinctOrders(materialLines);
}
